import { Alert, Button, Flex, Link, Text } from "@backstage/ui";
import {
	GITHUB_WEB_BASE,
	INFRA_REPO,
	INFRA_REPO_OWNER,
	holdMaxUntil,
	isArchived,
	type BundleInventoryItem,
} from "@internal/backstage-plugin-avd-common";
import { makeStyles } from "@material-ui/core/styles";
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { AdminHoldDialog } from "../avd/bundleHold";
import { PageSection, PageShell, StateCard } from "../layout/pageShell";

import { descriptorFor, type BundleInventoryDescriptor } from "./bundleDescriptors";
import { BundleDetailSheet } from "./bundleDetailSheet";
import { BundleInventoryTable, bundleKey, useBundleInventory } from "./bundleInventory";
import { ColleagueGroups } from "./ColleagueGroups";
import { BUNDLE_INVENTORY_DESCRIPTORS } from "./descriptors";
import { AttentionList, EstateFooter, EstateHero, MeasureTiles, PageSkeleton } from "./estateHero";
import { estateMeasures, needsAttention } from "./estateSummary";
import {
	clearFilters,
	hasFilters,
	isTileSelected,
	matchesFilters,
	parseFilters,
	toggleTile,
	toSearchParams,
	type InventoryFilters,
} from "./inventoryFilters";
import { ActiveFilterTags, InventoryToolbar } from "./inventoryToolbar";

// "Admin" — the platform team's view of every registered bundle type, from inside the portal rather
// than the Actions tab. It is a view of the REGISTRY (what Backstage believes); orphans, cost and
// entitlement drift stay with `azure-estate-report`, which the footer links. Gate and fail-loud rules:
// docs/app/avd-registry.md § The admin page.

const workflowUrl = (file: string) =>
	`${GITHUB_WEB_BASE}/${INFRA_REPO_OWNER}/${INFRA_REPO}/actions/workflows/${file}`;
const ESTATE_REPORT_URL = workflowUrl("azure-estate-report.yaml");
const REGISTRY_SYNC_URL = workflowUrl("avd-registry-sync.yaml");

const useStyles = makeStyles((theme) => ({
	notices: { display: "grid", gap: theme.spacing(1), marginBottom: theme.spacing(2) },
	controls: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: theme.spacing(1),
		flexWrap: "wrap",
		marginBottom: theme.spacing(1),
	},
	noMatch: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		gap: theme.spacing(1),
		padding: theme.spacing(4, 2),
		textAlign: "center",
	},
}));

/** A filter that matches nothing: distinct from an empty registry, with one way out. */
function NoMatches(props: Readonly<{ onClear: () => void }>): ReactNode {
	const classes = useStyles();
	return (
		<div className={classes.noMatch}>
			<Text as="h2" variant="title-small">
				No bundles match these filters.
			</Text>
			<Text as="p" color="secondary">
				Try removing one or more filters to see the estate again.
			</Text>
			<Button variant="primary" size="small" onPress={props.onClear}>
				Clear filters
			</Button>
		</div>
	);
}

/** What the table shows with no rows: an incomplete read, or a filter that matches nothing. */
function emptyContent(total: number, failed: boolean, onClear: () => void): ReactNode {
	if (total === 0 && !failed) {
		return (
			<Text as="p" color="secondary">
				No bundle rows are registered yet. Colleague bases are, and By colleague lists them.
			</Text>
		);
	}
	if (total === 0 && failed) {
		return (
			<Text as="p" color="secondary">
				Some bundle types could not be read, so this table is incomplete — not empty. See
				the warning above, then refresh.
			</Text>
		);
	}
	return <NoMatches onClear={onClear} />;
}

/** The inventory of every registered bundle type, filtered by what the URL says. */
export const BundleAdminPage = (
	props: Readonly<{ descriptors?: readonly BundleInventoryDescriptor[] }>,
) => {
	const classes = useStyles();
	const descriptors = props.descriptors ?? BUNDLE_INVENTORY_DESCRIPTORS;
	const { items, types, bases, refreshedAt, error, loading, refresh } = useBundleInventory();
	const [searchParams, setSearchParams] = useSearchParams();
	const filters = useMemo(() => parseFilters(searchParams), [searchParams]);
	const setFilters = useCallback(
		(next: InventoryFilters) => setSearchParams(toSearchParams(next), { replace: true }),
		[setSearchParams],
	);
	// The sheet follows a bundle across reads, not the row a filter happens to show.
	const [openKey, setOpenKey] = useState<string>();
	const [hold, setHold] = useState<{ key: string; max: string }>();
	const open = useCallback((item: BundleInventoryItem) => setOpenKey(bundleKey(item)), []);
	const opened = openKey ? items?.find((i) => bundleKey(i) === openKey) : undefined;
	// A bundle that left the read must not come back as an open sheet when a later read has it again.
	useEffect(() => {
		if (openKey && items && !opened) setOpenKey(undefined);
		if (hold && items && !items.some((i) => bundleKey(i) === hold.key)) setHold(undefined);
	}, [openKey, hold, items, opened]);
	const holdTrigger = useRef<HTMLElement | null>(null);
	const closeHold = useCallback(() => {
		setHold(undefined);
		// Back to the card's button, or to the section if a refresh removed the card.
		requestAnimationFrame(() => {
			const trigger = holdTrigger.current;
			(trigger?.isConnected
				? trigger
				: document.querySelector<HTMLElement>('[aria-label="Needs attention"]')
			)?.focus();
		});
	}, []);
	// Read when a bundle opens (or its data refreshes), not when the page last rendered: a hold may have expired since.
	const sheetNow = useMemo(() => new Date(), [opened]);

	// One instant per render, so every relative time agrees with the others.
	const now = new Date();

	if (loading && !items) {
		return (
			<PageShell>
				<PageSkeleton />
			</PageShell>
		);
	}

	// No table at all when the first read fails: an empty grid would claim nobody has anything.
	if (!items) {
		return (
			<PageShell>
				<EstateHero anyLive={false} now={now} loading={loading} />
				<StateCard
					role="alert"
					title="Couldn't read the bundle registry"
					action={
						<Button variant="primary" onPress={refresh} isDisabled={loading}>
							Try again
						</Button>
					}
				>
					{error ?? "The bundle registry is unreachable."}
				</StateCard>
			</PageShell>
		);
	}

	const failedTypes = types.filter((t) => t.failed);
	const unavailable = types.filter((t) => !t.available && !t.failed);
	const anyLive = types.some((t) => t.readAt);
	// An archived row is out of the estate: it counts in no tile and asks for no decision.
	const live = items.filter((item) => !isArchived(item));
	const measures = estateMeasures(live, descriptors, now);
	const attention = needsAttention(live, descriptors, now);
	const rows = items.filter((item) =>
		matchesFilters(item, filters, descriptorFor(descriptors, item.bundleType)),
	);
	const empty = emptyContent(items.length, failedTypes.length > 0, () =>
		setFilters(clearFilters(filters)),
	);
	const holdItem = hold ? items.find((i) => bundleKey(i) === hold.key) : undefined;
	const registryEmpty = items.length === 0 && bases.length === 0 && failedTypes.length === 0;

	const notices = (
		<div className={classes.notices}>
			{error ? (
				<Alert
					icon
					status="warning"
					role="alert"
					title="Couldn't refresh — showing the last good read"
					description={error}
				/>
			) : null}
			{/* A source that threw is a failure, not a quiet type: say what is and is not on screen. */}
			{failedTypes.length ? (
				<Alert
					icon
					status="warning"
					role="alert"
					title={`Couldn't read ${failedTypes.map((t) => t.displayName).join(", ")}`}
					description="Its rows are the last good read, or missing if there was none. A missing row here does not mean the bundle is gone."
				/>
			) : null}
			{/* Said once, not on every row: a live read that failed would otherwise look like quiet. */}
			{unavailable.length ? (
				<Alert
					icon
					status="info"
					role="status"
					title={`Showing the registry only for ${unavailable.map((t) => t.displayName).join(", ")}`}
					description="The live read of this type failed or has no reader configured, so its live state (such as power) and its own drift checks are unavailable. This means it could not be queried, not that anything is switched off. For AVD, Backstage needs its reader identity to have Reader on the management group holding the vended subscriptions."
				/>
			) : null}
		</div>
	);

	const hero = (
		<EstateHero
			measures={measures}
			refreshedAt={refreshedAt}
			anyLive={anyLive}
			now={now}
			loading={loading}
			onRefresh={refresh}
		/>
	);

	if (registryEmpty) {
		return (
			<PageShell>
				{hero}
				{notices}
				<StateCard title="No bundles yet">
					The registry has no rows yet — nothing has been provisioned through Backstage,
					and no backfill has run.{" "}
					<Link href={REGISTRY_SYNC_URL}>Run the registry sync</Link> to seed it from the
					Azure estate.
				</StateCard>
				<EstateFooter
					estateReportUrl={ESTATE_REPORT_URL}
					registrySyncUrl={REGISTRY_SYNC_URL}
				/>
			</PageShell>
		);
	}

	return (
		<PageShell>
			{hero}
			{notices}

			<PageSection
				eyebrow="At a glance"
				title="Where the estate stands"
				note="Select a measure to focus the table"
			>
				<MeasureTiles
					measures={measures}
					filters={filters}
					onToggle={(tile) => setFilters(toggleTile(filters, tile))}
				/>
			</PageSection>

			{attention.total > 0 ? (
				<PageSection eyebrow="Decisions first" title="Needs attention" note="Failed first">
					<Flex direction="column" gap="2">
						<AttentionList
							entries={attention.entries}
							types={types}
							descriptors={descriptors}
							now={now}
							onOpen={open}
							onHold={(item, trigger) => {
								holdTrigger.current = trigger ?? null;
								setHold({
									key: bundleKey(item),
									max: holdMaxUntil("admin", new Date()).toISOString(),
								});
							}}
						/>
						{attention.total > attention.entries.length ? (
							<div>
								<Button
									variant="tertiary"
									size="small"
									onPress={() =>
										setFilters(
											isTileSelected(filters, "attention")
												? filters
												: toggleTile(filters, "attention"),
										)
									}
								>
									Show all {attention.total}
								</Button>
							</div>
						) : null}
					</Flex>
				</PageSection>
			) : null}

			<PageSection
				eyebrow="Complete view"
				title="All bundles"
				note={`${rows.length} of ${items.length} ${items.length === 1 ? "bundle" : "bundles"}`}
			>
				<div className={classes.controls}>
					<InventoryToolbar
						items={items}
						types={types}
						filters={filters}
						descriptors={descriptors}
						onChange={setFilters}
					/>
				</div>
				<ActiveFilterTags
					items={items}
					types={types}
					filters={filters}
					descriptors={descriptors}
					onChange={setFilters}
				/>
				{filters.group === "colleague" ? (
					<ColleagueGroups
						items={rows}
						bases={bases}
						types={types}
						descriptors={descriptors}
						now={now}
						emptyContent={empty}
						includeBaseOnly={!hasFilters(filters)}
						onOpen={open}
					/>
				) : (
					<BundleInventoryTable
						items={rows}
						types={types}
						descriptors={descriptors}
						now={now}
						emptyContent={empty}
						onOpen={open}
					/>
				)}
			</PageSection>

			<EstateFooter estateReportUrl={ESTATE_REPORT_URL} registrySyncUrl={REGISTRY_SYNC_URL} />

			{opened ? (
				<BundleDetailSheet
					item={opened}
					types={types}
					descriptors={descriptors}
					now={sheetNow}
					onClose={() => setOpenKey(undefined)}
					onChanged={refresh}
				/>
			) : null}
			{holdItem ? (
				<AdminHoldDialog
					bundleType={holdItem.bundleType}
					bundleId={holdItem.bundleId}
					slug={holdItem.slug}
					holdReason={holdItem.holdReason}
					max={hold?.max}
					onClose={closeHold}
					onChanged={refresh}
				/>
			) : null}
		</PageShell>
	);
};
